import { AfterViewInit, ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { buildProviders } from '@kalidea/kaligraphi';
import { Select, Store } from '@ngxs/store';
import { CleRepartitionModelDto, PlanAnalytiqueModelDto } from 'src/graphql/generated-types';
import { Observable, of } from 'rxjs';
import { startWith, tap } from 'rxjs/operators';
import {
    AnalyticalPlanAndSections,
    AnalyticalPlanState
} from 'src/app/_shared/stores/analytical-plan/analytical-plan.state';
import { KalTableHeader } from 'src/app/_shared/modules/kal-table/kal-table.module';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { arrayEmpty, arrayOnlyOne } from 'src/app/utils/array.utils';


/**
 * Converti la valeur de AnalyticalPlanComponent en tableau de CleRepartitionModelDto
 */
export const generateDistributionKeys = (distribution: {
    [idPlan: string]: {
        section: number,
        plan: number,
        amount: number,
        percentage: number,
        planLabel: string
    }[]
}): CleRepartitionModelDto[] => {
    const distributions = Object.keys(distribution).reduce((acc, key) => acc.concat(distribution[key]), []);

    return distributions.filter(({amount}) => amount > 0).map(({plan, planLabel, percentage, amount, section}) => {
        return {
            IdPlanAnalytique: plan,
            LibellePlanAnalytique: planLabel,
            Pourcentage: percentage / 100,
            MontantARepartir: amount,
            IdSectionAnalytique: section
        };
    });
};

@Component({
    selector: 'app-analytical-plan-distributer',
    templateUrl: './analytical-plan-distributer.component.html',
    styleUrls: ['./analytical-plan-distributer.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(AnalyticalPlanDistributerComponent),
    standalone: false
})
export class AnalyticalPlanDistributerComponent extends CustomFormControlAccess implements OnInit, AfterViewInit {

    @Select(AnalyticalPlanState.loading) loading$: Observable<boolean>;

    overpaymentAmount: number;

    readonly columns: KalTableHeader<{ account: number, percentage: number, amount: number, actions: void }>[] = [
        {label: 'Compte', code: 'account'},
        {label: 'Pourcentage', code: 'percentage'},
        {label: 'Montant', code: 'amount'},
        {label: '', code: 'actions'}
    ];

    readonly dataSources: { [planId: number]: AbstractControl[] } = {};

    analyticalPlans: AnalyticalPlanAndSections[];

    private form: FormGroup;

    constructor(private readonly store: Store,
                private readonly fb: FormBuilder) {
        super();
    }

    private _accountId: number = null;

    @Input()
    get accountId(): number {
        return this._accountId;
    }

    set accountId(accountId: number) {
        this._accountId = accountId;
    }

    validate(c: AbstractControl): Observable<{}> {
        return of(this.form.valid ? null : {analyticalrepartition: true});
    }

    addRow(plan: PlanAnalytiqueModelDto, options: { basePercentage?, sectionId? } = {}): void {
        const formArray = this.form.get('' + plan.Id) as FormArray;

        if (!options.basePercentage) {
            options.basePercentage = formArray.controls.reduce((missingPercentage, c) => missingPercentage - +c.get('percentage').value, 100);
        }

        const analyticalRow = this.createAnalyticalRowControl(plan.Id, options);

        this.watch$ = analyticalRow.valueChanges.pipe(
            this.untilDestroyed,
            tap(() => formArray.updateValueAndValidity())
        );

        formArray.controls.push(analyticalRow);
        this.dataSources[plan.Id] = [...formArray.controls];

        formArray.updateValueAndValidity();
    }

    removeRow(plan: PlanAnalytiqueModelDto, row: FormGroup): void {
        const formArray = this.form.get('' + plan.Id) as FormArray;

        formArray.controls.splice(formArray.controls.indexOf(row), 1);
        this.dataSources[plan.Id] = [...formArray.controls];
        formArray.updateValueAndValidity();
    }

    private reqiredAnalyticalPlanValidator(): ValidatorFn | null {
        return (formArray: FormArray) => {
            return arrayEmpty(formArray.value) || formArray.value.some(value => !value.section) ? {invalidsectionid: true} : null;
        };
    }

    private analyticalPlanValidator(overpaidAmount): ValidatorFn | null {
        return (formArray: FormArray) => {
            const sum = formArray.controls.reduce((acc, c) => {
                acc.amount += +c.value.amount;
                acc.percentage += +c.value.percentage;
                return acc;
            }, {amount: 0, percentage: 0});

            if (!sum || sum.percentage !== 100 || sum.amount !== overpaidAmount) {
                return {invaliddistribution: true};
            }
            return null;
        };
    }

    private createAnalyticalRowControl(planId: number, {
        basePercentage = 0,
        sectionId
    }: { basePercentage?, sectionId? }): FormGroup {
        const analyticalRow = this.fb.group({
            section: [sectionId || null],
            percentage: [basePercentage, [Validators.max(100)]],
            amount: [+this.overpaymentAmount.toFixed(2) * basePercentage / 100, [Validators.max(+this.overpaymentAmount.toFixed(2))]],
            planLabel: [this.analyticalPlans.find(ap => ap.plan.Id === planId).plan.Libelle],
            plan: [planId]
        });

        const percentageControl = analyticalRow.get('percentage');
        const amountControl = analyticalRow.get('amount');

        // maj du montant en fonction du %
        this.watch$ = percentageControl.valueChanges.pipe(
            this.untilDestroyed,
            startWith('' + basePercentage),
            tap(value => {
                const newAmount = Math.round(+this.overpaymentAmount.toFixed(2) * (+value)) / 100;
                amountControl.patchValue(newAmount, {emitEvent: false});
            })
        );

        // maj du % en fonction du montant
        this.watch$ = amountControl.valueChanges.pipe(
            this.untilDestroyed,
            tap(value => {
                const newPercentage = (+value / +this.overpaymentAmount.toFixed(2)) * 100;
                percentageControl.patchValue(newPercentage);
            })
        );

        return analyticalRow;
    }

    private initPlanFormData(planAndSections: AnalyticalPlanAndSections): void {
        const {plan} = planAndSections;
        if (this.form.contains(`${plan.Id}`)) {
            return;
        }

        const formArray = new FormArray([], {validators: [this.reqiredAnalyticalPlanValidator(), this.analyticalPlanValidator(+this.overpaymentAmount.toFixed(2))]});
        this.form.addControl('' + plan.Id, formArray);

        this.dataSources[plan.Id] = [];

        // Lookup keys, to generate rows.
        plan.ListeClesRepartition.CleRepartitionModelDTO.forEach(k => {
            this.addRow(plan, {
                basePercentage: (k.Pourcentage * 100) || 0,
                sectionId: k.IdSectionAnalytique
            });
        });

        if (arrayOnlyOne(formArray.controls)) {
            formArray.controls[0].patchValue({percentage: 100});
        }
    }

    ngOnInit(): void {
        this.form = this.fb.group({});

        this.watch$ = this.store.select(AnalyticalPlanState.overpaymentAmount).pipe(
            tap(overpaymentAmount => this.overpaymentAmount = overpaymentAmount)
        );

        this.watch$ = this.store.select(AnalyticalPlanState.analyticalPlansAndSections).pipe(
            tap(analyticalPlansAndSections => {
                this.analyticalPlans = analyticalPlansAndSections || [];
                this.analyticalPlans.forEach(p => this.initPlanFormData(p));
            })
        );
    }

    ngAfterViewInit(): void {
        this.watch$ = this.form.valueChanges.pipe(
            startWith(this.form.value as object), // as object retire la depreciation sur startWith
            tap(value => this.notifyUpdate(value))
        );
    }
}
